import { jwtDecode } from "jwt-decode";


const BASE_URL = "https://quagganwheeloffortune.dataduck.dk/api/";
const LOGIN_ENDPOINT = "auth/login";
const REGISTER_ENDPOINT = "auth/register";
const REFRESH_ENDPOINT = "auth/refresh";

// Generic HTTP error handler used by all API calls
// - Reads body once as text to avoid stream reuse issues
// - If response is JSON and parseable, returns parsed JSON
// - Otherwise returns plain text (e.g. "API key saved")
async function handleHttpErrors(res) {
  const contentType = res.headers.get("content-type") || "";
  const text = await res.text();

  if (!res.ok) {
    return Promise.reject({ status: res.status, fullError: text });
  }

  if (!text) {
    return null;
  }

  if (contentType.includes("application/json")) {
    try {
      return JSON.parse(text);
    } catch (e) {
      // Fall back to raw text if body isn't valid JSON
      return text;
    }
  }

  return text;
}

const setToken = (token) => {
  localStorage.setItem("jwtToken", token);
};
const getToken = () => {
  return localStorage.getItem("jwtToken");
};
const loggedIn = () => {
  return getToken() != null;
};
const logout = () => {
  localStorage.removeItem("jwtToken");
};
const getUsername = () => {
  const token = getToken();
  if (!token) return null;
  try {
    const decoded = jwtDecode(token);
    return decoded.username || decoded.sub || decoded.name || null;
  } catch (e) {
    console.error("Failed to decode token", e);
    return null;
  }
};

const toNumericId = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && /^\d+$/.test(value.trim())) {
    return Number(value.trim());
  }
  return null;
};

const extractUserId = (userLike) => {
  if (!userLike || typeof userLike !== "object") return null;

  const directCandidates = [
    userLike.id,
    userLike.userId,
    userLike.userID,
    userLike.userid,
    userLike.user_id,
    userLike.ID,
  ];

  for (const candidate of directCandidates) {
    const resolved = toNumericId(candidate);
    if (resolved !== null) return resolved;
  }

  if (userLike.user && typeof userLike.user === "object") {
    const nestedResolved = extractUserId(userLike.user);
    if (nestedResolved !== null) return nestedResolved;
  }

  if (userLike.data && typeof userLike.data === "object") {
    const nestedResolved = extractUserId(userLike.data);
    if (nestedResolved !== null) return nestedResolved;
  }

  return null;
};

const getLoggedInUserId = () => {
  const token = getToken();
  if (!token) return null;

  try {
    const decoded = jwtDecode(token);
    const claimCandidates = [
      decoded.id,
      decoded.userId,
      decoded.userID,
      decoded.userid,
      decoded.user_id,
      decoded.ID,
      decoded.sub,
    ];

    for (const candidate of claimCandidates) {
      const resolved = toNumericId(candidate);
      if (resolved !== null) return resolved;
    }
  } catch (e) {
    console.error("Failed to decode token user id", e);
  }

  return null;
};

// Extract roles/authorities from the JWT token in a flexible way
const getRoles = () => {
  const token = getToken();
  if (!token) return [];
  try {
    const decoded = jwtDecode(token);
    let roles = [];

    if (Array.isArray(decoded.roles)) {
      roles = roles.concat(decoded.roles);
    } else if (typeof decoded.roles === "string") {
      roles = roles.concat(decoded.roles.split(/[\s,]+/));
    }

    if (Array.isArray(decoded.authorities)) {
      decoded.authorities.forEach((a) => {
        if (typeof a === "string") {
          roles.push(a);
        } else if (a && typeof a.authority === "string") {
          roles.push(a.authority);
        }
      });
    }

    if (decoded.role && typeof decoded.role === "string") {
      roles.push(decoded.role);
    }

    const normalized = roles
      .filter(Boolean)
      .map((r) => String(r).toUpperCase());

    return [...new Set(normalized)];
  } catch (e) {
    console.error("Failed to decode token roles", e);
    return [];
  }
};

// Check if the current user has at least one of the required roles
const hasAnyRole = (requiredRoles = []) => {
  const roles = getRoles();
  if (!roles.length || !requiredRoles.length) return false;
  const wanted = requiredRoles.map((r) => String(r).toUpperCase());
  return roles.some((r) => wanted.includes(r));
};

const refreshJwt = () => {
  const options = makeOptions("POST", false);
  return fetch(BASE_URL + REFRESH_ENDPOINT, options)
    .then(handleHttpErrors)
    .then((res) => {
      if (!res || !res.token) {
        return Promise.reject({ status: 500, fullError: "Refresh endpoint returned no token" });
      }
      setToken(res.token);
      return res.token;
    });
};

const fetchWithRefreshRetry = async (endpoint, buildOptions, allowRefresh = false) => {
  const response = await fetch(BASE_URL + endpoint, buildOptions());

  if (response.status === 403 && allowRefresh) {
    await refreshJwt();
    const retryResponse = await fetch(BASE_URL + endpoint, buildOptions());
    return handleHttpErrors(retryResponse);
  }

  return handleHttpErrors(response);
};

const callApi = (method, endpoint, addToken, body) => {
  const allowRefresh = Boolean(addToken);
  return fetchWithRefreshRetry(endpoint, () => makeOptions(method, addToken, body), allowRefresh);
};

const login = (username, password) => {
  const options = makeOptions("POST", false, { username, password });
  return fetchWithRefreshRetry(LOGIN_ENDPOINT, () => options, false)
    .then((res) => {
      setToken(res.token);
    });
};

const register = (username, password) => {
  const options = makeOptions("POST", false, { username, password });
  return fetchWithRefreshRetry(REGISTER_ENDPOINT, () => options, false)
    .then((res) => {
      setToken(res.token);
    });
};

// --- GW2 API key management ---
// Backend will determine the user from the JWT token, so we don't send username
const setApiKey = async (apiKey) => {
  return callApi("POST", "auth/setapikey", true, { apiKey });
};

const deleteApiKey = async () => {
  return callApi("DELETE", "auth/deleteapikey", true);
};

// --- QWOF API endpoints ---
const getAccount = () => {
  return callApi("GET", "gw2/account", true);
};
const getUser = (username) => {
  return callApi("GET", `qwof/user/${username}`, true)
    .catch((err) => {
      // If the user profile does not exist yet, treat it as "no profile" instead of a hard error
      if (err && err.status === 404) {
        return null;
      }
      throw err;
    });
};
const getGuild = (id) => {
  return callApi("GET", `qwof/guild/${id}`, true);
};
const getGuildMembers = (id) => {
  return callApi("GET", `qwof/guild/${id}/members`, true);
};
const getGuildMember = (id, username) => {
  const encoded = encodeURIComponent(username || "");
  return callApi("GET", `qwof/guild/${id}/member/${encoded}`, true).catch((err) => {
    // If server returns 404, surface null to caller for graceful handling
    if (err && err.status === 404) return null;
    throw err;
  });
};
const isMemberOfGuild = (id, username) => {
  return callApi("GET", `qwof/guild/${id}/ismember/${username}`, true);
};
const getGuildStash = (id) => {
  return callApi("GET", `qwof/guild/${id}/stash`, true);
};
const getGuildStashTab = (id, tabId) => {
  return callApi("GET", `qwof/guild/${id}/stash/${tabId}`, true);
};
const setNickname = (username, nickname) => {
  return callApi("POST", `qwof/user/${username}/nickname`, true, { nickname });
};

const postGame = (game) => {
  return callApi("POST", "qwof/game", true, game);
};

const getGames = () => {
  return callApi("GET", "qwof/game", false);
};

const updateGame = (id, game) => {
  return callApi("PUT", `qwof/game/${id}`, true, game);
};

const deleteGame = (id) => {
  return callApi("DELETE", `qwof/game/${id}`, true);
};

const getNonparticipants = () => {
  return callApi("GET", "qwof/nonparticipant", false);
};

const getNonparticipant = (id) => {
  return callApi("GET", `qwof/nonparticipant/${id}`, false);
};

const createNonparticipant = (nonparticipant) => {
  return callApi("POST", "qwof/nonparticipant", true, nonparticipant);
};

const createNonparticipants = (list) => {
  return callApi("POST", "qwof/nonparticipant/batch", true, list);
};

const updateNonparticipant = (id, nonparticipant) => {
  return callApi("PUT", `qwof/nonparticipant/${id}`, true, nonparticipant);
};

const deleteNonparticipant = (id) => {
  return callApi("DELETE", `qwof/nonparticipant/${id}`, true);
};

const getItems = () => {
  return callApi("GET", "qwof/item", false);
};

const searchItems = (searchTerm = "", type = "", page = 0, limit = 20) => {
  const params = new URLSearchParams();
  if (searchTerm) params.append("search", searchTerm);
  if (type) params.append("type", type);
  params.append("page", page);
  params.append("limit", limit);
  
  const queryString = params.toString();
  const endpoint = queryString ? `qwof/item?${queryString}` : "qwof/item";
  
  return callApi("GET", endpoint, false);
};

const searchGw2Items = () => {
  return callApi("POST", "gw2/item/sync", true);
};

// --- Admin: GW2 guild sync endpoints ---
const syncGuild = (id) => {
  return callApi("POST", `gw2/guild/${id}/sync`, true);
};

const syncGuildMembers = (id) => {
  return callApi("POST", `gw2/guild/${id}/members/sync`, true);
};

const syncGuildStash = (id) => {
  return callApi("POST", `gw2/guild/${id}/stash/sync`, true);
};

// --- Guild Member Management ---
const addGuildAliases = (gw2Username, aliases = []) => {
  return callApi("POST", `qwof/guild/member/${encodeURIComponent(gw2Username || "")}/alias`, true, aliases);
};

const deleteGuildAliases = (gw2Username, aliasIds = []) => {
  return callApi("DELETE", `qwof/guild/member/${encodeURIComponent(gw2Username || "")}/alias`, true, aliasIds);
};

// Backwards-compatible single-alias helpers still point at the bulk endpoints.
const addAlias = (gw2Username, aliasName, isPreferred = false) => {
  return addGuildAliases(gw2Username, [{ aliasName, isIgnored: false, isPreferred }]);
};

const deleteAlias = (gw2Username, aliasId) => {
  const payload = typeof aliasId === "object" && aliasId !== null ? [aliasId] : [{ id: aliasId }];
  return deleteGuildAliases(gw2Username, payload);
};

const makeOptions = (method, addToken, body) => {
  var opts = {
    method: method,
    headers: {
      Accept: "application/json",
    },
  };
  if (addToken && loggedIn()) {
    opts.headers["Authorization"] = `Bearer ${getToken()}`;
  }
  if (body !== undefined) {
    opts.headers["Content-type"] = "application/json";
    opts.body = JSON.stringify(body);
  }
  return opts;
};

const facade = {
  makeOptions,
  setToken,
  getToken,
  loggedIn,
  login,
  register,
  logout,
  getUsername,
  extractUserId,
  getLoggedInUserId,
  getRoles,
  hasAnyRole,
  setApiKey,
  deleteApiKey,
  getAccount,
  getUser,
  getGuild,
  getGuildMembers,
  isMemberOfGuild,
  getGuildStash,
  getGuildStashTab,
  setNickname,
  postGame,
  getGames,
  updateGame,
  deleteGame,
  getNonparticipants,
  getNonparticipant,
  createNonparticipant,
  createNonparticipants,
  updateNonparticipant,
  deleteNonparticipant,
  getItems,
  searchItems,
  searchGw2Items,
  syncGuild,
  syncGuildMembers,
  syncGuildStash,
  addGuildAliases,
  deleteGuildAliases,
  addAlias,
  deleteAlias,
  getGuildMember,
};

export default facade;
